import { Building2, Cpu, Search } from "lucide-react";
import {
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import type { SearchHit } from "@busright/data-contract";

import { useSearch } from "@/hooks/queries";

// Functional global search. Debounced query → /api/search → a results dropdown grouped by kind
// (devices, recipients). Arrow keys move a highlight, Enter opens it, Escape closes. Each hit
// navigates: a device → its Device-360 (/device/:serial); a recipient/district → the Recipient
// page (/recipient/:id), which resolves the id into a Scope and renders ScopedEntityPage. Per
// feedback the global bar was effectively broken (recipients had nowhere useful to land) — this
// makes it actually work for district + device search; per-page scoped search is a later step.

const KIND_LABEL: Record<SearchHit["kind"], string> = {
  device: "Devices",
  recipient: "Recipients",
};

const KIND_ICON: Record<SearchHit["kind"], typeof Cpu> = {
  device: Cpu,
  recipient: Building2,
};

// Where a given hit navigates. Devices reach the Device-360; recipients/districts reach the
// Recipient page, which resolves the id into a Scope via ScopedEntityPage.
const hitTarget = (hit: SearchHit): string =>
  hit.kind === "device"
    ? `/device/${encodeURIComponent(hit.id)}`
    : `/recipient/${encodeURIComponent(hit.id)}`;

// Min chars before the dropdown opens (mirrors useSearch's fetch threshold).
const MIN_QUERY_CHARS = 2;
// Defer blur-close so a click on a result lands before the dropdown unmounts.
const BLUR_CLOSE_MS = 120;
// Stable id linking the combobox input to its results listbox (aria-controls).
const RESULTS_ID = "global-search-results";

interface HitGroup {
  readonly kind: SearchHit["kind"];
  readonly hits: readonly SearchHit[];
}

// Stable group order (devices first), preserving the server's within-group ranking.
const GROUP_ORDER: readonly SearchHit["kind"][] = ["device", "recipient"];

const groupHits = (hits: readonly SearchHit[]): readonly HitGroup[] =>
  GROUP_ORDER.map((kind) => ({ kind, hits: hits.filter((h) => h.kind === kind) })).filter(
    (g) => g.hits.length > 0,
  );

export const GlobalSearch = (): ReactNode => {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { data, isFetching } = useSearch(value);
  const hits = useMemo(() => data?.data.hits ?? [], [data]);
  const groups = useMemo(() => groupHits(hits), [hits]);

  // Flat, render-order list so a single index can drive both keyboard nav and the highlight class.
  const flatHits = useMemo(() => groups.flatMap((g) => g.hits), [groups]);

  const trimmed = value.trim();
  const showDropdown = open && trimmed.length >= MIN_QUERY_CHARS;

  const choose = (hit: SearchHit): void => {
    setOpen(false);
    setValue("");
    navigate(hitTarget(hit));
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValue(event.target.value);
    setOpen(true);
    setActiveIdx(0);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (flatHits.length === 0) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIdx((i) => (i + 1) % flatHits.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIdx((i) => (i - 1 + flatHits.length) % flatHits.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const hit = flatHits[activeIdx];
      if (hit) {
        choose(hit);
      }
    }
  };

  // Close on blur, but defer so a click on a result lands before the dropdown unmounts.
  const onBlur = (): void => {
    blurTimer.current = setTimeout(() => setOpen(false), BLUR_CLOSE_MS);
  };
  const onFocus = (): void => {
    if (blurTimer.current) {
      clearTimeout(blurTimer.current);
    }
    if (trimmed.length >= MIN_QUERY_CHARS) {
      setOpen(true);
    }
  };

  return (
    <div className="global-search">
      <Search className="search-icon" size={15} aria-hidden />
      <input
        type="text"
        role="combobox"
        placeholder="Search devices, districts, serials…"
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        aria-label="Global search"
        aria-expanded={showDropdown}
        aria-controls={RESULTS_ID}
        aria-autocomplete="list"
        autoComplete="off"
        spellCheck={false}
      />
      {showDropdown ? (
        <div id={RESULTS_ID} className="search-results" role="listbox" aria-label="Search results">
          {flatHits.length > 0 ? (
            groups.map((group) => {
              const Icon = KIND_ICON[group.kind];
              return (
                <div key={group.kind} className="search-group">
                  <div className="search-group-head">{KIND_LABEL[group.kind]}</div>
                  {group.hits.map((hit) => {
                    const idx = flatHits.indexOf(hit);
                    return (
                      <button
                        key={`${hit.kind}-${hit.id}`}
                        type="button"
                        role="option"
                        aria-selected={idx === activeIdx}
                        className={`search-result${idx === activeIdx ? " active" : ""}`}
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => choose(hit)}
                      >
                        <Icon size={14} aria-hidden className="search-result-icon" />
                        <span className="search-result-text">
                          <span className="search-result-label">{hit.label}</span>
                          {hit.sublabel ? (
                            <span className="search-result-sub">{hit.sublabel}</span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })
          ) : (
            <div className="search-empty">
              {isFetching ? "Searching…" : `No matches for “${trimmed}”`}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};
